import * as React from 'react'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import { oneMinuteInMs } from '../../../shared/lib/duration.no-deps'
import { mapMerge, mapSet } from '../../../shared/lib/functional/map.no-deps'
import { timeout } from '../../../shared/lib/timeout.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { Pulse } from '../ui/Pulse'
import { canBeRead, findUpload, type Upload } from '../upload/Upload'
import { type PromiseState, promiseStateRender } from '../util/PromiseState'

const RETRY_TIMEOUT = oneMinuteInMs

type ProcessState = 'waiting-for-disk-write' | 'processing' | 'cancelled'
export type FileUrlStates = Map<FileId, PromiseState<string>>

interface Props {
    deps: CommonDeps
    fileIds: FileId[]
    uploads: Upload[]
    readBlob: (fileId: FileId) => Promise<Blob>
    render: (urlStates: FileUrlStates) => React.ReactElement<unknown>
}

interface State {
    objectUrls: FileUrlStates
    remoteUrls: FileUrlStates
}

/**
 * Component to manage loading up the urls for files.
 * For local files, need to create and destory object url safely to avoid memory leak
 * For remote files, need to poll the server continuously until the file has been uploaded
 */
export class FileUrlManager extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    public state: State = {
        objectUrls: new Map(),
        remoteUrls: new Map(),
    }

    public componentDidMount() {
        this.processFiles()
    }

    public componentDidUpdate() {
        this.processFiles()
    }

    public componentWillUnmount() {
        for (const [_, state] of this.state.objectUrls) {
            if (state.status === 'in-progress') state.cancel()
            if (state.status === 'resolved') URL.revokeObjectURL(state.value)
        }
        for (const [_, state] of this.state.remoteUrls) {
            if (state.status === 'in-progress') state.cancel()
        }
    }

    private processStates = new Map<FileId, ProcessState>()

    private processFiles() {
        for (const fileId of this.props.fileIds) {
            const processState = this.processStates.get(fileId)
            const upload = findUpload(this.props.uploads, fileId)

            // If the file is already been handled, ignore
            if (processState === 'processing') continue

            // If the file is waiting for upload to change, check if it has
            if (processState === 'waiting-for-disk-write') {
                this.checkIfDiskWritten(fileId, upload)
                continue
            }

            // Otherwise treat as new file and start processing
            this.processStates.set(fileId, 'processing')

            // If it has an upload
            if (upload != null) {
                // Process as a local file
                this.processLocalFile(fileId, upload)
            } else {
                // Process as a remote file
                this.processRemoteFile(fileId)
            }
        }
    }

    private async checkIfDiskWritten(fileId: FileId, upload: Upload) {
        try {
            // Ignore if disk still not written
            if (!canBeRead(upload)) return

            // Otherwise start 'processing'
            this.processStates.set(fileId, 'processing')

            // And read the local file
            await this.readLocalFile(fileId)
        } catch (error) {
            // If cancelled then ignore error
            if (this.processStates.get(fileId) === 'cancelled') return

            // Otherwise update promise state to error state
            this.updateObjectUrlPromiseState(fileId, { status: 'rejected', error })
        }
    }

    private async processLocalFile(fileId: FileId, upload: Upload) {
        try {
            const cancel = () => this.processStates.set(fileId, 'cancelled')

            // Set the promise state for local file to 'in-progress'
            this.updateObjectUrlPromiseState(fileId, { status: 'in-progress', cancel })

            // If not written to disk yet, don't try to read blob yet
            if (!canBeRead(upload)) {
                this.processStates.set(fileId, 'waiting-for-disk-write')
                return
            }

            // Otherwise read the file
            await this.readLocalFile(fileId)
        } catch (error) {
            // If cancelled then ignore error
            if (this.processStates.get(fileId) === 'cancelled') return

            // Otherwise update promise state to error state
            this.updateObjectUrlPromiseState(fileId, { status: 'rejected', error })
        }
    }

    private async readLocalFile(fileId: FileId) {
        // Read the file
        const blob = await this.props.readBlob(fileId)

        // If cancelled then ignore and let blob be garbage collected
        if (this.processStates.get(fileId) === 'cancelled') return

        // Otherwise continue to create object url
        const objectUrl = URL.createObjectURL(blob)

        // And update promise state
        this.updateObjectUrlPromiseState(fileId, { status: 'resolved', value: objectUrl })
    }

    private async processRemoteFile(fileId: FileId) {
        try {
            // Avoid this being handled twice
            this.processStates.set(fileId, 'processing')

            const cancel = () => this.processStates.set(fileId, 'cancelled')

            // Set the promise state for remote file to 'in-progress'
            this.updateRemoteUrlPromiseState(fileId, { status: 'in-progress', cancel })

            // Loop infinitely unless cancelled
            while (this.processStates.get(fileId) === 'processing') {
                try {
                    // Try to fetch the value
                    const remoteUrl = (await this.props.deps.httpClient.getFile(fileId, {
                        doNotRedirect: true,
                    })) as string

                    // Do not cancelled, don't update
                    if (this.processStates.get(fileId) === 'cancelled') return

                    // Updated the promise state with file url
                    this.updateRemoteUrlPromiseState(fileId, { status: 'resolved', value: remoteUrl })

                    // Stop looping
                    return
                } catch (error) {
                    // Do not log failure
                }

                // Wait to loop round again
                await timeout(RETRY_TIMEOUT)
            }
        } catch (error) {
            // If cancelled then ignore error
            if (this.processStates.get(fileId) === 'cancelled') return

            // Otherwise update promise state to error state
            this.updateRemoteUrlPromiseState(fileId, { status: 'rejected', error })
        }
    }

    private updateObjectUrlPromiseState(fileId: FileId, state: PromiseState<string>) {
        this.setState({ objectUrls: mapSet(this.state.objectUrls, fileId, state) })
    }

    private updateRemoteUrlPromiseState(fileId: FileId, state: PromiseState<string>) {
        this.setState({ remoteUrls: mapSet(this.state.remoteUrls, fileId, state) })
    }

    private tryRender = (): React.ReactElement<unknown> => {
        return this.props.render(mapMerge(this.state.objectUrls, this.state.remoteUrls))
    }
}

/**
 * Helper to render with the file url inside a fileUrlStates map
 */
export function withFileUrl(
    deps: CommonDeps,
    fileUrlStates: FileUrlStates,
    fileId: FileId,
    render: (url: string | null) => React.ReactElement<unknown>,
    loading?: () => React.ReactElement<unknown>
) {
    if (fileId == null) return render(null)

    const fileUrlState = fileUrlStates.get(fileId)
    if (fileUrlState == null) return render(null)

    return promiseStateRender(deps, fileUrlState, render, { inProgress: loading ?? Pulse })
}
